import React, { useEffect, useState } from "react";
import { Col, Radio, Row, Select } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

// graphql
import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";
import useBannerBackground from "hooks/useBannerBackground";
import GET_LANGUAGES from "graphql/queries/GetLanguages";
import GET_COURSES from "graphql/queries/GetCourses";
import GET_COURSE_INFO from "graphql/queries/GetCourseInfo";

// custom types
import { Level, Language } from "types";

// import types for antd components
import { SelectValue } from "antd/lib/select";
import { RadioChangeEvent } from "antd/lib/radio";

// styling
import * as Layout from "common/layout";
import help from "assets/help.png";
import LanguageFilterCommon from "common/language_filter";
import { filterActiveLevelsAndCurrentUserLevel } from "utils/language";
import {
  BannerSection,
  CourseBannerTitle,
  StyledSelector,
  StyledFilter,
  SelectorContainer,
  CourseDetails,
} from "./BookACourseStyle";

const { SectionHeader, HeaderCol, PageSection } = Layout;
const { Option } = Select;

type CourseBannerProps = {
  setCourseList: (courseList: any) => void;
  setLoadingCourseList: (courseList: any) => void;
  courseFromLocationId: number | undefined;
  setIsHighestLevel: (state: boolean) => void;
};

type CourseFilter = {
  levelId?: number;
  languageId?: string;
  courseType?: string;
  courseDays?: string;
  courseTime?: string;
};

const CourseBanner = ({
  courseFromLocationId,
  // setCurrentCourseId, // eslint-disable-line @typescript-eslint/no-unused-vars
  setCourseList, // eslint-disable-line @typescript-eslint/no-unused-vars
  setLoadingCourseList,
  setIsHighestLevel,
}: CourseBannerProps) => {
  const { t } = useTranslation("book_a_course");
  const [filter, setFilter] = useState<CourseFilter>({
    levelId: undefined,
    languageId: undefined,
    courseType: "standard",
    courseDays: "weekdays",
    courseTime: "ongoing",
  });

  const { data: languagesData } = useQuery(GET_LANGUAGES);

  const changeFilter = (newFilter: CourseFilter) => {
    setFilter({
      ...filter,
      ...newFilter,
    });
  };

  const { data: currentUserInfo, loading: loadingUserInfo } = useQuery(
    GET_CURRENT_USER_INFO,
    {
      fetchPolicy: "network-only",
      onCompleted: ({ me: { currentLevel, currentLanguage } }) => {
        if (!filter.levelId && !filter.languageId) {
          changeFilter({
            languageId: currentLanguage.id,
            levelId: currentLevel.id,
          });
        }
      },
    }
  );

  const onLanguageFieldChange = (value: string) => {
    const studyLanguage = languagesData.languages.find((language: Language) => {
      return language.id === value;
    });

    changeFilter({
      languageId: studyLanguage?.id,
      levelId: studyLanguage?.levels.find((level: Level) => level.active)?.id,
    });
  };

  const manuallyChangeFilter = (newFilter: CourseFilter) => {
    changeFilter(newFilter);
  };

  const { data: courseList, loading: loadingCourseList } = useQuery(
    GET_COURSES,
    {
      skip: !filter.levelId || !filter.courseType,
      variables: {
        levelId: filter.levelId,
        courseType: filter.courseType,
        courseDays: filter.courseDays,
        languageId: undefined,
      },
      fetchPolicy: "network-only",
      onCompleted: () => {
        setCourseList(courseList.courses);
        const studyLanguage = languagesData.languages.find(
          (language: Language) => {
            return language.id === filter.languageId;
          }
        );
        const isHighestLevel =
          studyLanguage.levels[studyLanguage.levels.length - 1] ===
          filter.levelId;
        setIsHighestLevel(isHighestLevel);
      },
    }
  );

  useEffect(() => {
    setLoadingCourseList(loadingCourseList);
  }, [loadingCourseList, setLoadingCourseList]);

  useEffect(() => {
    if (courseList && filter.courseTime === "ongoing") {
      const filteredCourseList = courseList.courses?.filter(
        (course: any) => course.ongoing === true
      );
      setCourseList(filteredCourseList);
    } else if (courseList && filter.courseTime === "future") {
      const filteredCourseList = courseList.courses?.filter(
        (course: any) => course.future === true
      );
      setCourseList(filteredCourseList);
    }
  }, [filter.courseTime, courseList, setCourseList]);

  const { bannerBackgroundUrl } = useBannerBackground();
  // Set default level equal to user's current level

  // Set default filter accourding to selected course
  useQuery(GET_COURSE_INFO, {
    skip: !courseFromLocationId,
    variables: {
      id: courseFromLocationId,
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data) {
        const { courseType, levelId, courseDays } = data.courseDetails;
        changeFilter({
          courseType,
          levelId,
          courseDays,
          courseTime: "ongoing",
          languageId: undefined,
        });
      }
    },
  });

  if (languagesData && filter.levelId && !filter.languageId) {
    const studyLanguage = languagesData.languages.find((language: Language) => {
      return language.levels.some(
        (level: { id: number }) => level.id === filter.levelId
      );
    });
    changeFilter({
      languageId: studyLanguage.id,
    });
  }

  const studyLanguage = languagesData?.languages.find(
    (language: { id: string }) => {
      return language.id === filter.languageId;
    }
  );

  const levelOptions = filterActiveLevelsAndCurrentUserLevel(
    currentUserInfo?.me?.currentLevel?.id,
    studyLanguage?.levels
  );

  return (
    <>
      <BannerSection background-url={bannerBackgroundUrl}>
        <SectionHeader>
          <HeaderCol xs={21}>
            <CourseBannerTitle>{t("BOOK A FULL COURSE")}</CourseBannerTitle>
          </HeaderCol>
          <HeaderCol xs={3} style={{ flex: "none" }}>
            <a
              href="https://flexiclasses.com/faq-topic/booking-classes/"
              target="_blank"
            >
              <button type="button" style={{ all: "unset", cursor: "pointer" }}>
                <img src={help} alt="Help" style={{ width: "60px" }} />
              </button>
            </a>
          </HeaderCol>
        </SectionHeader>
      </BannerSection>
      <PageSection style={{ padding: "0 8%", marginTop: "20px" }}>
        <Row
          gutter={{ xs: 0, sm: 0, md: 0, lg: 12, xl: 24 }}
          style={{ marginBottom: "10px" }}
        >
          <Col xs={24} lg={5} xl={7} xxl={6}>
            <LanguageFilterCommon
              onChange={onLanguageFieldChange}
              filterLanguageId={
                filter.languageId || languagesData?.languages[0].id
              }
              languages={languagesData?.languages}
              isLeftAlign
              dropIcon={<CaretDownOutlined className="value-dropdown-icon" />}
              disabled={loadingCourseList}
            />
          </Col>
          <Col xs={0} lg={19} />
        </Row>
        <Row gutter={{ xs: 0, sm: 0, md: 0, lg: 12, xl: 24 }}>
          <Col xs={24} lg={5} xl={7} xxl={6} style={{ marginBottom: "10px" }}>
            <SelectorContainer>
              <StyledSelector
                suffixIcon={<CaretDownOutlined />}
                placeholder={t("My Current Level")}
                value={filter.levelId}
                optionFilterProp="children"
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(value: SelectValue) =>
                  manuallyChangeFilter({
                    levelId: value as number,
                  })
                }
                dropdownStyle={{ textAlign: "left" }}
                loading={loadingUserInfo}
                disabled={loadingCourseList}
              >
                {levelOptions.map((level: Level) => (
                  <Option
                    value={level.id}
                    key={level.id}
                    disabled={!level.active}
                  >
                    {level.title}
                  </Option>
                ))}
              </StyledSelector>
            </SelectorContainer>
          </Col>
          <Col xs={24} lg={5} xl={4} xxl={6} style={{ marginBottom: "10px" }}>
            <StyledFilter
              value={filter.courseTime}
              onChange={(ev: RadioChangeEvent) => {
                manuallyChangeFilter({
                  courseTime: ev.target.value as string,
                });
              }}
              disabled={loadingCourseList}
            >
              <Radio.Button value="ongoing">{t("Ongoing")}</Radio.Button>
              <Radio.Button value="future">{t("Future")}</Radio.Button>
            </StyledFilter>
          </Col>
          <Col xs={24} lg={8} xl={8} xxl={6} style={{ marginBottom: "10px" }}>
            <StyledFilter
              value={filter.courseType}
              onChange={(ev: RadioChangeEvent) => {
                manuallyChangeFilter({
                  courseType: ev.target.value as string,
                });
              }}
              disabled={loadingCourseList}
            >
              <Radio.Button value="standard">
                {t("Standard 4h/wk")}
              </Radio.Button>
              <Radio.Button value="intensive">
                {t("Intensive 10h/wk")}
              </Radio.Button>
            </StyledFilter>
          </Col>
          <Col xs={24} lg={6} xl={5} xxl={6} style={{ marginBottom: "10px" }}>
            <StyledFilter
              value={filter.courseDays}
              onChange={(ev: RadioChangeEvent) =>
                manuallyChangeFilter({
                  courseDays: ev.target.value as string,
                })
              }
              disabled={loadingCourseList}
            >
              <Radio.Button value="weekdays">{t("Weekdays")}</Radio.Button>
              <Radio.Button value="weekends">{t("Weekends")}</Radio.Button>
            </StyledFilter>
          </Col>
        </Row>
        <CourseDetails>
          All course times are displayed in the time zone you selected in
          <a href="/account/my-settings"> My Settings.</a>
        </CourseDetails>
      </PageSection>
    </>
  );
};

export default CourseBanner;
